/**
 * The phone-number sticker offered on the way to the cart (spec #107,
 * ticket #113).
 *
 * It is an ordinary Standard sheet — nothing about it is special to the
 * print pipeline, the ladder or fulfillment. One text line holding the
 * number, white background, black text, no icon: the same shape the
 * configurator URL codec expresses (`?l1=…&bg=white&tc=black&s=medium`), so
 * the design could equally be a prefilled configurator link.
 *
 * Pure and client-safe. The cart page uses it to validate the input and to
 * decide whether the cart already holds a phone sticker; the server action
 * uses the same helpers to rebuild the configuration from the number alone,
 * so the design is never taken from the client.
 */

import { STANDARD_SIZE_ID } from "@/domain/size-contract";
import type { SingleStickerConfiguration, StickerConfiguration } from "@/domain/types";
import { LINE_MAX_LENGTH } from "@/domain/validation";
import type { ShopifyCartLine } from "@/integrations/shopify";
import { getStickerConfigurationFromCartLine } from "@/integrations/shopify/cart-lines";

/**
 * Everything but the number. Ids are the Supabase design-asset rows
 * (`design_backgrounds.white`, `design_text_colors.black`); a missing row
 * would only fail inside the render lock, so they are pinned here once.
 * Inter is the plainest sans in the font list — digits read unambiguously.
 */
export const PHONE_STICKER_DESIGN = {
  font: "inter",
  background: "white",
  textColor: "black",
  size: STANDARD_SIZE_ID,
  icon: null,
} as const satisfies Pick<
  SingleStickerConfiguration,
  "font" | "background" | "textColor" | "size" | "icon"
>;

/**
 * The soft check's only addition to the spec's character rule (#113: digits,
 * spaces, hyphens, a leading "+", within the line limit). The spec's rule
 * alone would accept "+", "-" or " - ", which print as a sticker with no
 * number on it; requiring a few digits stops exactly that and nothing a real
 * number would trip. Three because the shortest numbers anyone puts on a
 * sticker are three digits (112, short numbers); a mistyped but plausible
 * number is the buyer's call, not the form's.
 */
const PHONE_MIN_DIGITS = 3;

/**
 * Stricter than the input check, on purpose: this decides whether a sticker
 * already in the cart *is* a phone sticker, which suppresses the offer. An
 * ordinary name sticker reading "007" or "12" must not count, so detection
 * keeps the six digits a real phone number has.
 */
const PHONE_STICKER_DETECTION_MIN_DIGITS = 6;

const PHONE_CHARACTERS_RE = /^\+?[0-9 -]+$/;

/** The number as it will be printed: edge whitespace trimmed, inner runs collapsed. */
export function normalizePhoneStickerNumber(raw: string): string {
  return raw.trim().replace(/\s+/g, " ");
}

function countDigits(value: string): number {
  return value.replace(/\D/g, "").length;
}

/** The spec's character rule: digits, spaces, hyphens, a leading "+", within the line limit. */
function hasPhoneStickerShape(value: string): boolean {
  return value.length > 0 && value.length <= LINE_MAX_LENGTH && PHONE_CHARACTERS_RE.test(value);
}

/**
 * Soft validation: digits, spaces, hyphens and a single leading "+", within
 * the sticker's line limit, plus at least `PHONE_MIN_DIGITS` digits so the
 * sticker has a number on it. Soft because numbers are written many ways
 * ("070-123 45 67", "+46 70 123 45 67"); the check only stops what would
 * obviously misprint.
 */
export function isValidPhoneStickerNumber(raw: string): boolean {
  const value = normalizePhoneStickerNumber(raw);
  return hasPhoneStickerShape(value) && countDigits(value) >= PHONE_MIN_DIGITS;
}

/** One Standard sheet with the number on line 1. Assumes a valid number. */
export function buildPhoneStickerConfiguration(raw: string): SingleStickerConfiguration {
  return {
    kind: "single",
    lines: { line1: normalizePhoneStickerNumber(raw), line2: "", line3: "" },
    ...PHONE_STICKER_DESIGN,
    quantity: 1,
  };
}

/**
 * A sticker whose only text is a phone number. Deliberately looser than the
 * offered design — font, colours, size and kind are ignored — because a
 * buyer who already made a blue number sticker in the configurator has a
 * phone sticker, and offering another would be the nag the spec rules out.
 */
export function isPhoneStickerConfiguration(configuration: StickerConfiguration): boolean {
  if (configuration.icon !== null) {
    return false;
  }

  const { line1, line2, line3 } = configuration.lines;
  const filled = [line1, line2, line3].map((line) => line.trim()).filter(Boolean);

  if (filled.length !== 1) {
    return false;
  }
  const value = normalizePhoneStickerNumber(filled[0]);
  return hasPhoneStickerShape(value) && countDigits(value) >= PHONE_STICKER_DETECTION_MIN_DIGITS;
}

/** True when any line's stored configuration is a phone sticker. */
export function cartHasPhoneSticker(
  lines: ReadonlyArray<Pick<ShopifyCartLine, "attributes" | "quantity" | "stickerSnapshot">>
): boolean {
  return lines.some((line) => {
    const configuration = getStickerConfigurationFromCartLine(line);
    return configuration !== null && isPhoneStickerConfiguration(configuration);
  });
}
